import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import { PQRSD_MAX_INTERIMS_PER_CYCLE, PQRSD_RESPONSE_TYPE } from "../../constants";
import { useSelector } from "react-redux";

import { startResponseFlow } from "../../services/backend/PqrsdService";
import { getPqrsdConfig } from "../../services/backend/PqrsdConfigService";
import { getReciversUser } from "../../services/backend/UserService";
import {
  MAX_MIDDLE_STEPS,
  MIDDLE_STEP_TYPES,
  availableAnswerTemplates,
  chainProblem,
  selectableUsers,
  stepsPayload,
  userLabel,
} from "./responseChainDraft";

import { pqrsdText } from "./pqrsdLabel";
// Starts the supervised answer to a PQRSD: which answer template (Config →
// PQRSD → Documentos) and who elaborates, reviews/approves and signs it. The
// chain is chosen here every time; nothing about it is stored in config.

function UserPicker({ label, users, loading, value, onChange, disabled }) {
  return (
    <Autocomplete
      sx={{ flex: 1, minWidth: 220 }}
      options={users}
      loading={loading}
      getOptionLabel={userLabel}
      isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
      value={value || null}
      onChange={(_, v) => onChange(v || null)}
      renderInput={(params) => <TextField {...params} label={label} required size="small" />}
      noOptionsText="Sin resultados"
      disabled={disabled}
    />
  );
}

export default function StartResponseFlowDialog({
  open,
  onClose,
  parentRadicadoId,
  parentSubject,
  onStarted,
  interimCountThisCycle = 0,
  // Per-institution interim cap (config.maxInterimsPerCycle). 0 = unlimited.
  maxInterims = PQRSD_MAX_INTERIMS_PER_CYCLE,
}) {
  const user = useSelector((s) => s.user.value);

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [elaborator, setElaborator] = useState(null);
  // [{ key, type: 'review'|'approve', user }]
  const [middle, setMiddle] = useState([]);
  const [signer, setSigner] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [responseType, setResponseType] = useState(PQRSD_RESPONSE_TYPE.FINAL);

  const interimUnlimited = Number(maxInterims) === 0;
  const interimDisabled = !interimUnlimited && interimCountThisCycle >= maxInterims;

  useEffect(() => {
    if (!open) return;
    let active = true;
    setError(null);
    setTemplateId("");
    setMiddle([]);
    setSigner(null);
    setElaborator(null);
    setSubject(parentSubject ? `Respuesta a ${parentRadicadoId}: ${parentSubject}` : "");
    setResponseType(PQRSD_RESPONSE_TYPE.FINAL);

    (async () => {
      setLoadingTemplates(true);
      const cfg = await getPqrsdConfig();
      if (!active) return;
      const list = availableAnswerTemplates(cfg?.documents);
      setTemplates(list);
      // One template is the common case: nothing to choose.
      if (list.length === 1) setTemplateId(list[0].id);
      setLoadingTemplates(false);
    })();

    (async () => {
      setLoadingUsers(true);
      const res = await getReciversUser(user.token);
      if (!active) return;
      const list = selectableUsers(res?.users || res?.result || (Array.isArray(res) ? res : []));
      setUsers(list);
      // Whoever starts the answer usually writes it.
      setElaborator(list.find((u) => u.userId === user.userId) || null);
      setLoadingUsers(false);
    })();

    return () => { active = false; };
  }, [open, user, parentRadicadoId, parentSubject]);

  const problem = useMemo(() => chainProblem({ elaborator, middle, signer }), [elaborator, middle, signer]);
  const template = templates.find((t) => t.id === templateId) || null;
  const canSubmit = !!template && !problem && !!subject.trim() && !submitting;

  const addMiddle = () => {
    if (middle.length >= MAX_MIDDLE_STEPS) return;
    setMiddle((m) => [...m, { key: `${Date.now()}${Math.random()}`, type: "review", user: null }]);
  };
  const updateMiddle = (idx, patch) =>
    setMiddle((m) => m.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const removeMiddle = (idx) => setMiddle((m) => m.filter((_, i) => i !== idx));
  const moveMiddle = (idx, delta) =>
    setMiddle((m) => {
      const to = idx + delta;
      if (to < 0 || to >= m.length) return m;
      const next = [...m];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await startResponseFlow(
        {
          parentRadicadoId,
          answerTemplateId: template.id,
          steps: stepsPayload({ elaborator, middle, signer }),
          subject: subject.trim(),
          responseType,
        },
        user
      );
      if (onStarted) onStarted(res);
    } catch (e) {
      setError(e.message || "No fue posible iniciar el flujo");
    } finally {
      setSubmitting(false);
    }
  };

  const stepNumber = (i) => i + 1;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Iniciar respuesta supervisada</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pqrsdText("La respuesta a la PQRSD ")}<strong>{parentRadicadoId}</strong>{pqrsdText(" pasará por la cadena que definas antes de salir al ciudadano. Los datos de la PQRSD y del ciudadano se llenan solos en la plantilla.")}
        </Typography>

        <FormControl sx={{ mb: 2 }} disabled={submitting}>
          <FormLabel>Tipo de respuesta</FormLabel>
          <RadioGroup
            row
            value={responseType}
            onChange={(e) => setResponseType(e.target.value)}
          >
            <FormControlLabel
              value={PQRSD_RESPONSE_TYPE.FINAL}
              control={<Radio />}
              label="Respuesta final"
            />
            <FormControlLabel
              value={PQRSD_RESPONSE_TYPE.INTERIM}
              control={<Radio />}
              label="Respuesta interina (informe de avance)"
              disabled={interimDisabled}
            />
          </RadioGroup>
          <FormHelperText>
            {interimDisabled
              ? `Se alcanzó el límite de ${maxInterims} respuestas interinas en este ciclo. Solo es posible registrar la respuesta final.`
              : responseType === PQRSD_RESPONSE_TYPE.INTERIM
                ? (interimUnlimited
                  ? `Una respuesta interina reinicia el plazo y permite continuar la gestión. Llevas ${interimCountThisCycle} en este ciclo (sin límite).`
                  : `Una respuesta interina reinicia el plazo y permite continuar la gestión. Llevas ${interimCountThisCycle}/${maxInterims} en este ciclo.`)
                : pqrsdText("La respuesta final invita al ciudadano a cerrar la PQRSD o pedir más información.")}
          </FormHelperText>
        </FormControl>

        {!loadingTemplates && templates.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {pqrsdText("No hay plantillas de respuesta disponibles. Un administrador debe crear al menos una plantilla activa en Configuración → PQRSD → Documentos → Plantillas de respuesta.")}
          </Alert>
        ) : (
          <TextField
            select
            fullWidth
            label="Plantilla de respuesta"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={loadingTemplates || submitting}
            sx={{ mb: 2 }}
            helperText={
              loadingTemplates
                ? "Cargando plantillas..."
                : template?.stale
                  ? "Esta plantilla tiene cambios sin regenerar; se usará la última versión generada."
                  : ""
            }
          >
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}{t.stale ? " (desactualizada)" : ""}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          fullWidth
          required
          label="Asunto de la respuesta"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={submitting}
          inputProps={{ maxLength: 300 }}
          error={!subject.trim()}
          helperText={subject.trim() ? "" : "El asunto es obligatorio."}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }}>
          <Chip label="Cadena de aprobación" size="small" />
        </Divider>

        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Box sx={{ minWidth: 170 }}>
              <Typography variant="subtitle2">Paso {stepNumber(0)}: Elaborar</Typography>
              <Typography variant="caption" color="text.secondary">Redacta la respuesta</Typography>
            </Box>
            <UserPicker
              label="Elabora"
              users={users}
              loading={loadingUsers}
              value={elaborator}
              onChange={setElaborator}
              disabled={submitting}
            />
          </Stack>

          {middle.map((s, idx) => (
            <Stack key={s.key} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <Box sx={{ minWidth: 170, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>Paso {stepNumber(idx + 1)}:</Typography>
                <TextField
                  select
                  size="small"
                  value={s.type}
                  onChange={(e) => updateMiddle(idx, { type: e.target.value })}
                  disabled={submitting}
                  sx={{ minWidth: 130 }}
                >
                  {MIDDLE_STEP_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <UserPicker
                label={s.type === "approve" ? "Aprueba" : "Revisa"}
                users={users}
                loading={loadingUsers}
                value={s.user}
                onChange={(u) => updateMiddle(idx, { user: u })}
                disabled={submitting}
              />
              <Stack direction="row">
                <Tooltip title="Subir">
                  <span>
                    <IconButton size="small" onClick={() => moveMiddle(idx, -1)} disabled={submitting || idx === 0}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Bajar">
                  <span>
                    <IconButton size="small" onClick={() => moveMiddle(idx, 1)} disabled={submitting || idx === middle.length - 1}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Quitar paso">
                  <span>
                    <IconButton size="small" onClick={() => removeMiddle(idx)} disabled={submitting}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          ))}

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addMiddle}
              disabled={submitting || middle.length >= MAX_MIDDLE_STEPS}
            >
              Agregar revisión o aprobación
            </Button>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <Box sx={{ minWidth: 170 }}>
              <Typography variant="subtitle2">Paso {stepNumber(middle.length + 1)}: Firmar</Typography>
              <Typography variant="caption" color="text.secondary">Firma y radica la respuesta</Typography>
            </Box>
            <UserPicker
              label="Firma"
              users={users}
              loading={loadingUsers}
              value={signer}
              onChange={setSigner}
              disabled={submitting}
            />
          </Stack>
        </Stack>

        {problem && template && (
          <FormHelperText sx={{ mt: 2 }}>{problem}</FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? "Iniciando..." : "Iniciar flujo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
